import { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { CalendarClock, Clock, ChevronLeft, ChevronRight, CheckCircle2, Ban, Loader2, ArrowLeft } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const iso = (d) => d.toISOString().slice(0, 10);
const monthLabel = (d) => d.toLocaleDateString(undefined, { month: "long", year: "numeric" });

export default function PublicBooking() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [monthCursor, setMonthCursor] = useState(() => { const d = new Date(); d.setDate(1); return d; });
  const [selectedDate, setSelectedDate] = useState(null);
  const [slots, setSlots] = useState([]);
  const [slotReason, setSlotReason] = useState(null);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [step, setStep] = useState("pick"); // pick | form | done
  const [invitee, setInvitee] = useState({ name: "", email: "" });
  const [answers, setAnswers] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [confirmed, setConfirmed] = useState(null);

  useEffect(() => {
    axios.get(`${API}/public/template/${slug}`)
      .then((r) => setData(r.data))
      .catch(() => setNotFound(true));
  }, [slug]);

  const loadSlots = useCallback(async (dateStr) => {
    setLoadingSlots(true); setSlots([]); setSlotReason(null); setSelectedSlot(null);
    try {
      const r = await axios.get(`${API}/public/slots/${slug}`, { params: { day: dateStr } });
      setSlots(r.data.slots); setSlotReason(r.data.reason);
    } catch (e) { setSlotReason("error"); }
    finally { setLoadingSlots(false); }
  }, [slug]);

  const pickDate = (dateStr) => { setSelectedDate(dateStr); loadSlots(dateStr); };

  const submit = async () => {
    if (!invitee.name.trim() || !invitee.email.trim()) { toast.error("Enter your name and email"); return; }
    for (const q of data.template.questions) {
      if (q.required && !(answers[q.id] || "").toString().trim()) { toast.error(`Please answer: ${q.label}`); return; }
    }
    setSubmitting(true);
    try {
      const r = await axios.post(`${API}/public/book`, {
        slug, date: selectedDate, start_time: selectedSlot,
        invitee_name: invitee.name, invitee_email: invitee.email, answers,
      });
      setConfirmed(r.data); setStep("done");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not book");
    } finally { setSubmitting(false); }
  };

  if (notFound) return (
    <div className="min-h-screen flex items-center justify-center bg-white text-center px-6">
      <div>
        <Ban className="w-10 h-10 text-slate-300 mx-auto mb-3" />
        <h1 className="text-xl font-bold">Booking page not found</h1>
        <p className="text-slate-500 mt-1">This link may be inactive or incorrect.</p>
      </div>
    </div>
  );
  if (!data) return <div className="min-h-screen flex items-center justify-center bg-white"><Loader2 className="w-7 h-7 animate-spin text-blue-600" /></div>;

  const { template, owner } = data;

  // calendar grid
  const firstDay = new Date(monthCursor);
  const startWeekday = (firstDay.getDay() + 6) % 7; // Mon=0
  const daysInMonth = new Date(firstDay.getFullYear(), firstDay.getMonth() + 1, 0).getDate();
  const todayIso = iso(new Date());
  const cells = [];
  for (let i = 0; i < startWeekday; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) {
    const dd = new Date(firstDay.getFullYear(), firstDay.getMonth(), d);
    cells.push(iso(dd));
  }

  if (step === "done" && confirmed) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center px-6">
        <div className="max-w-md w-full text-center bg-white border border-slate-200 rounded-2xl shadow-sm p-10" data-testid="booking-confirmation">
          <div className="w-16 h-16 rounded-full bg-green-50 border border-green-200 flex items-center justify-center mx-auto mb-5">
            <CheckCircle2 className="w-9 h-9 text-green-600" />
          </div>
          <h1 className="text-2xl font-extrabold">You're booked!</h1>
          <p className="text-slate-600 mt-2">Your <strong>{template.name}</strong> with {owner.name} is confirmed.</p>
          <div className="mt-5 rounded-xl bg-blue-50 border border-blue-100 p-4 text-left">
            <p className="text-sm font-semibold text-blue-900">{new Date(confirmed.date + "T00:00:00").toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric" })}</p>
            <p className="text-sm text-blue-800">{confirmed.start_time}–{confirmed.end_time} ({template.duration_minutes} min)</p>
          </div>
          <p className="text-xs text-slate-500 mt-5">A confirmation email is on its way to {confirmed.invitee_email}.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8">
        <div className="flex items-center gap-2 mb-8">
          <div className="w-8 h-8 rounded-lg bg-blue-600 flex items-center justify-center"><CalendarClock className="w-4.5 h-4.5 text-white" /></div>
          <span className="text-lg font-extrabold tracking-tight">Cliq2Book</span>
        </div>

        <div className="grid md:grid-cols-3 gap-8">
          {/* left info */}
          <div className="md:col-span-1">
            <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-6 md:sticky md:top-8">
              {owner.picture && <img src={owner.picture} alt="" className="w-14 h-14 rounded-full mb-3 border border-slate-200" />}
              <p className="text-sm text-slate-500">{owner.name}</p>
              <h1 className="text-xl font-bold mt-0.5">{template.name}</h1>
              <div className="flex items-center gap-2 mt-3 text-sm text-slate-600">
                <Clock className="w-4 h-4 text-blue-600" /> {template.duration_minutes} minutes
              </div>
              {template.description && <p className="mt-4 text-sm text-slate-600 leading-relaxed">{template.description}</p>}
              <p className="mt-4 text-xs text-slate-400">Times shown in {data.timezone}.</p>
            </div>
          </div>

          {/* right picker/form */}
          <div className="md:col-span-2">
            {step === "pick" && (
              <div className="grid sm:grid-cols-2 gap-6">
                <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-5" data-testid="public-booking-date-picker">
                  <div className="flex items-center justify-between mb-4">
                    <button onClick={() => setMonthCursor(new Date(firstDay.getFullYear(), firstDay.getMonth() - 1, 1))}
                      className="p-1.5 rounded-lg hover:bg-slate-100" data-testid="cal-prev"><ChevronLeft className="w-4 h-4" /></button>
                    <span className="text-sm font-semibold">{monthLabel(monthCursor)}</span>
                    <button onClick={() => setMonthCursor(new Date(firstDay.getFullYear(), firstDay.getMonth() + 1, 1))}
                      className="p-1.5 rounded-lg hover:bg-slate-100" data-testid="cal-next"><ChevronRight className="w-4 h-4" /></button>
                  </div>
                  <div className="grid grid-cols-7 gap-1 text-center text-xs text-slate-400 mb-1">
                    {["M", "T", "W", "T", "F", "S", "S"].map((d, i) => <div key={i}>{d}</div>)}
                  </div>
                  <div className="grid grid-cols-7 gap-1">
                    {cells.map((c, i) => c === null ? <div key={i} /> : (
                      <button key={i} disabled={c < todayIso} onClick={() => pickDate(c)} data-testid={`cal-day-${c}`}
                        className={`aspect-square rounded-lg text-sm font-medium transition-colors ${
                          c < todayIso ? "text-slate-300 cursor-not-allowed"
                          : selectedDate === c ? "bg-blue-600 text-white"
                          : "hover:bg-blue-50 text-slate-700"
                        }`}>{parseInt(c.slice(-2), 10)}</button>
                    ))}
                  </div>
                </div>

                <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-5">
                  <p className="text-sm font-semibold mb-3">
                    {selectedDate ? new Date(selectedDate + "T00:00:00").toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric" }) : "Pick a date"}
                  </p>
                  {!selectedDate && <p className="text-sm text-slate-400">Select a day to see open times.</p>}
                  {loadingSlots && <div className="py-8 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-blue-600" /></div>}
                  {selectedDate && !loadingSlots && slots.length === 0 && (
                    <div className="rounded-lg bg-red-50 border border-red-200 p-4 text-sm text-red-800" data-testid="no-slots-message">
                      {slotReason === "cap_reached" && "Daily limit reached — Deep Work Mode engaged. Try another day."}
                      {slotReason === "deep_work_mode" && "Deep Work Mode is on. Try another day."}
                      {(!slotReason || slotReason === "invalid_date" || slotReason === "error") && "No optimal times available this day."}
                    </div>
                  )}
                  {!loadingSlots && slots.length > 0 && (
                    <div className="grid grid-cols-2 gap-2 max-h-[320px] overflow-y-auto pr-1">
                      {slots.map((s) => (
                        <button key={s} onClick={() => { setSelectedSlot(s); setStep("form"); }} data-testid="public-booking-time-slot-btn"
                          className={`px-3 py-2.5 rounded-lg border text-sm font-semibold transition-colors ${
                            selectedSlot === s ? "bg-blue-600 text-white border-blue-600" : "border-blue-200 text-blue-700 hover:bg-blue-50"
                          }`}>{s}</button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}

            {step === "form" && (
              <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-6" data-testid="public-booking-form">
                <button onClick={() => setStep("pick")} className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-800 mb-4"><ArrowLeft className="w-4 h-4" /> Back</button>
                <div className="rounded-lg bg-blue-50 border border-blue-100 p-3 mb-5 text-sm text-blue-900">
                  {new Date(selectedDate + "T00:00:00").toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric" })} · {selectedSlot} ({template.duration_minutes} min)
                </div>
                <div className="space-y-4">
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1.5">Your name *</label>
                      <input value={invitee.name} onChange={(e) => setInvitee({ ...invitee, name: e.target.value })} data-testid="invitee-name-input"
                        className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 outline-none" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1.5">Your email *</label>
                      <input type="email" value={invitee.email} onChange={(e) => setInvitee({ ...invitee, email: e.target.value })} data-testid="invitee-email-input"
                        className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 outline-none" />
                    </div>
                  </div>
                  {data.template.questions.map((q) => (
                    <div key={q.id}>
                      <label className="block text-sm font-medium text-slate-700 mb-1.5">{q.label}{q.required && " *"}</label>
                      {q.type === "textarea" ? (
                        <textarea value={answers[q.id] || ""} onChange={(e) => setAnswers({ ...answers, [q.id]: e.target.value })} rows={3} data-testid={`answer-${q.id}`}
                          className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 outline-none" />
                      ) : q.type === "dropdown" ? (
                        <select value={answers[q.id] || ""} onChange={(e) => setAnswers({ ...answers, [q.id]: e.target.value })} data-testid={`answer-${q.id}`}
                          className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 outline-none">
                          <option value="">Select…</option>
                          {(q.options || []).map((o) => <option key={o} value={o}>{o}</option>)}
                        </select>
                      ) : q.type === "checkbox" ? (
                        <label className="flex items-center gap-2 text-sm text-slate-700">
                          <input type="checkbox" checked={answers[q.id] === "Yes"} onChange={(e) => setAnswers({ ...answers, [q.id]: e.target.checked ? "Yes" : "No" })} data-testid={`answer-${q.id}`} /> Yes
                        </label>
                      ) : (
                        <input value={answers[q.id] || ""} onChange={(e) => setAnswers({ ...answers, [q.id]: e.target.value })} data-testid={`answer-${q.id}`}
                          className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 outline-none" />
                      )}
                    </div>
                  ))}
                  <button onClick={submit} disabled={submitting} className="brand-btn w-full py-3" data-testid="public-booking-submit-btn">
                    {submitting ? "Booking…" : "Confirm booking"}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
